/*    
<summary>
   This is the Truck Component which shows the Trucks Data.
   Developer: Aashish Singh, Created Date:04-mar-2024
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useState, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { ColumnType } from "../../constants/enums/gridColumnTypeEnum";
import { useStore } from "../../contexts/StoreProvider";
import useWindowDimensions, {
  getTableMaxHeight,
} from "../../helpers/windowResize.helper";
import { ISorting } from "../../models/ISorting";
import Grid, { getColumns } from "../../shared-components/Grid/Grid";
import SearchField from "../../shared-components/SearchField/SearchField";
import { formatMessage } from "../../translations/formatMessage";
import Pagination from "../../shared-components/Grid/Pagination";
import IPageInfo from "../../models/IPageInfo";
import { IActionFormatter, IFormatter } from "../../models/IFormatter";
import {
  PageLimitOptions,
  initialPageLimit,
} from "../../constants/pageLimit.constant";
import { Button, Container, Dropdown } from "react-bootstrap";
import DeletePopup from "../../shared-components/Popup/DeletePopup";
import DeletePopupHOC from "../../shared-components/Popup/Hoc/DeletePopupHoc";
import AddPopupHOC from "../../shared-components/Popup/Hoc/AddPopupHoc";
import EditPopupHOC from "../../shared-components/Popup/Hoc/EditPopupHoc";
import { Col, ListGroup, Row } from "react-bootstrap";
import { ITruck, ITruckList } from "../../models/response/ITruckResponse";
import "./Tanks.scss";
import Footer from "../../shared-components/Footer/Footer";
import SubHeader from "../../shared-components/Header/SubHeader";
import SlidingPanel from "react-sliding-side-panel";
import { FilterPopupHOC } from "../../shared-components/Filters/FilterPopupHOC";
import {
  isOperationPermitted,
  showIcons,
} from "../../helpers/routesFunctions.helper";
import {
  getClientId,
  getTableView,
  getUserType,
} from "../../helpers/localStorages.helper";
import { ModuleName } from "../../constants/moduleName.constant";
import SpinnerLoader from "../../shared-components/Loader/SpinnerLoader";
import NoData from "../../assets/Images/no_record.png";
import _, { filter } from "lodash";
import AddUpdateTruck from "./component/add-edit-trucks";
import TruckFilterForm from "./forms/truck-filter-form";
import { truckInitialFilterState } from "../../core/initialState/initial-filter-states";
import userPageLimitSettingEnum from "../../constants/enums/user-pagination-setting-enum";
import UserType from "../../constants/userType.constant";
import UrlConstants from "../../constants/url.constant";
import SelectAssetIdsPopupHOC from "./hoc/SelectAssetsIdsPopupHoc";
import SelectAssetIdForm from "./forms/select-asset-form";
import TruckDetails from "./component/truck-details";

interface IProps {
  tabKey: string;
  setTabKey: (k: string) => void;
  size: {
    height: number;
  };
  showDeletePopup: boolean;
  deleteToggleHandler: () => void;
  showAddPopup: boolean;
  addToggleHandler: () => void;
  showEditPopup: boolean;
  editToggleHandler: () => void;
  showFilterPopup: boolean;
  filterToggleHandler: () => void;
  showSelectAssetIdsPopup: boolean;
  selectAssetIdsToggleHandler: () => void;
}

const Truck: React.FC<IProps> = (props: IProps) => {
  const { truckStore, preferencesStore, customerStore } = useStore();
  const {
    tabKey,
    setTabKey,
    showDeletePopup,
    deleteToggleHandler,
    showAddPopup,
    addToggleHandler,
    showFilterPopup,
    filterToggleHandler,
    selectAssetIdsToggleHandler,
    showSelectAssetIdsPopup,
  } = props;
  const {
    inProgress,
    error,
    truckList,
    GetTruckListService,
    getTruckList,
    reset,
    resetError,
    DeleteTruckService,
    DeleteTruckState,
    resetDeleteTruckState,
    addUpdateTruckState,
    resetGetTruckDetail,
    setFilterDetail,
    filter,
    allTruckSelectOptions,
    sendAssetInfoTruckState,
    SendDownlinkAssetInformation,
    ReadDownLinkAssetInformationForAssetId,
    SendReadDownLinkAssetInformation,
    resetreadAssetInfoTruckStateDetails,
  } = truckStore;
  const {
    setUpdateTableView,
    CSVFileDownloadPostService,
    userPageLimitSetting,
    setAmountDecimal,
    setUserPageLimitSetting,
    setSelectedClientId,
    selectedClientId,
    allAvailableClientOptions,
    resetClientListState,
    clientList,
    setEnableLimitSettings,
    GetTruckSummaryService,
    getTruckOptionsVehicle,
  } = preferencesStore;
  const closePanelHandler = () => {
    setSelectedRow(undefined);
    setSelectedCard(-1);
    setOpenPanel(false);
    resetGetTruckDetail();
    resetreadAssetInfoTruckStateDetails();
  };
  const { GetAllCustomerService } = customerStore;
  const [tableView, setTableView] = useState<boolean>(
    getTableView() ? (getTableView() == "false" ? false : true) : false
  );
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [selectedRow, setSelectedRow] = useState<ITruckList | undefined>(
    undefined
  );
  const [openPanel, setOpenPanel] = useState(false);
  const [changeDetails, setChangeDetails] = useState<boolean>(false);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const truckListData: Array<ITruckList> = getTruckList;
  const [pageLimit, setPageLimit] = useState<number>(
    userPageLimitSetting.tankLimitConfig
  );
  const [searchText, setSearchText] = useState("");
  const [sorting, setSorting] = useState<ISorting>({
    column: "DeviceId",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
  });

  const { height, width } = useWindowDimensions();

  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);

  const onPageLimitChangedForUser = (
    evt: React.ChangeEvent<HTMLSelectElement>
  ) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
    setUserPageLimitSetting(userPageLimitSettingEnum.TankLimitConfig, value);
  };

  const onSearchTextChanged = (text: string) => {
    setSearchText(text);
  };

  // const exportFilteredLogsCSV = () => {
  //   let tempFilterDetails = { ..._.omit(filter, ["isFilterActive"]) };
  //   const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
  //   const url = `${UrlConstants.TruckCSVDownload}?clientId=${clientIdFromLocalStorage}`;
  //   tempFilterDetails = {
  //     ...tempFilterDetails,
  //     DLExpiryInDays:
  //       tempFilterDetails.DLExpiryInDays == -1
  //         ? null
  //         : tempFilterDetails.DLExpiryInDays,
  //   };
  //   CSVFileDownloadPostService(url, "Truck", tempFilterDetails);
  // };

  /**
   * This function calls the "GetTruckListService" store funtion that get truck list from API
   */
  const callGetTruckList = useCallback(() => {
    setDisablePagination(true);

    if (getUserType() === UserType.SuperAdmin) {
      if (selectedClientId > 0 && clientList.length > 0) {
        GetTruckListService(
          pageInfo.currentPage,
          pageLimit,
          sorting.orderBy,
          sorting.column,
          _.omit(filter, ["isFilterActive"])
        );
      }
    } else {
      GetTruckListService(
        pageInfo.currentPage,
        pageLimit,
        sorting.orderBy,
        sorting.column,
        _.omit(filter, ["isFilterActive"])
      );
    }
    setIsApiCalled(true);
  }, [
    GetTruckListService,
    pageInfo.currentPage,
    pageLimit,
    sorting.orderBy,
    sorting.column,
    filter,
    selectedClientId,
    clientList.length,
  ]);

  useEffect(() => {
    return () => {
      resetFilterHandler();
      resetClientListState();
    };
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "filter" of the useEffect changed.
   */
  // useEffect(() => {
  //   if (selectedClientId > 0 && filter.isFilterActive) {
  //     if (getUserType() == UserType.SuperAdmin) {
  //       if (selectedClientId != -1) callGetTruckList();
  //       else return;
  //     } else callGetTruckList();
  //   }
  //   else {
  //     if (getUserType() == UserType.SuperAdmin) {
  //       if (selectedClientId != -1) callGetTruckList();
  //       else return;
  //     } else callGetTruckList();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filter, selectedClientId, callGetTruckList]);

  useEffect(() => {
    if (selectedClientId > 0) callGetTruckList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, callGetTruckList]);

  const [selectedCard, setSelectedCard] = useState<number>(-1);

  useEffect(() => {
    setPageInfo((pageInfo) => ({
      ...pageInfo,
      totalPages: truckList?.TotalPages,
    }));
    setDisablePagination(false);
  }, [getTruckList, truckList.TotalPages]);

  useEffect(() => {
    if (addUpdateTruckState.success) {
      callGetTruckList();
    }
  }, [addUpdateTruckState.success, callGetTruckList]);

  useEffect(() => {
    if (error) {
      toast.error(formatMessage(error));
      resetError();
    }
  }, [error, resetError]);

  useEffect(() => {
    if (DeleteTruckState.success) {
      toast.success(formatMessage("deleted_success"));
      resetDeleteTruckState();
      deleteToggleHandler();
      callGetTruckList();
    }
  }, [DeleteTruckState.success, reset, callGetTruckList]);

  useEffect(() => {
    if (DeleteTruckState.error) {
      toast.error(formatMessage(DeleteTruckState.error));
      resetDeleteTruckState();
    }
  }, [DeleteTruckState.error, resetDeleteTruckState]);

  /**
   * This function shows the add truck pop up from where we can add new truck and update existing truck. we use id to identify
   * that we are adding or updating truck.
   * "Id == -1" : we are adding new truck
   * "Id != -1" : means we are updating existing truck
   */
  const openAddUpdateHandler = (row: { Id: number }) => {
    GetAllCustomerService();
    setSelectedId(row.Id);
    setChangeDetails(false);
    addToggleHandler();
  };
  const selectAssetIdHandler = () => {
    GetTruckSummaryService();
    selectAssetIdsToggleHandler();
  };

  /**
   * This function shows the delete truck confirmation popup component and sets the seleted truck Id
   */
  const deleteClickHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    setChangeDetails(false);
    deleteToggleHandler();
  };

  /**
   * This function deletes the truck by providing Id to the Api
   */
  const deleteHandler = () => {
    DeleteTruckService(selectedId);
  };

  /**
   * This function shows the detail truck panel
   */
  const showDetailsClickHandler = (row: ITruckList) => {
    setSelectedId(row.Id);
    setSelectedRow(row);
    // setChangeDetails(true);
    ReadDownLinkAssetInformationForAssetId(row, allAvailableClientOptions);
    // !showUserDetail && showUserDetailToggleHandler();
    // setIsApiCalled(false);
    setOpenPanel(true);
  };

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const rowEvents = (row: any) => {
    setSelectedCard(row.TruckId);
    setSelectedRow(row);
    setOpenPanel(true);
  };

  const tableViewToggleHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    let tableView =
      localStorage.getItem("secure_fuel_tableView") == "false"
        ? "true"
        : "false";
    setUpdateTableView(tableView);
    if (tableView == "false") {
      setTableView(false);
    } else {
      setTableView(true);
    }
  };

  const exportFilteredLogsCSV = () => {
    const clientIdFromLocalStorage = getClientId() != null ? getClientId() : -1;
    const url = `${UrlConstants.TruckCSVDownload}?clientId=${clientIdFromLocalStorage}`;
    let tempFilterDetails = { ..._.omit(filter, ["isFilterActive"]) };
    tempFilterDetails = {
      ...tempFilterDetails,
    };
    CSVFileDownloadPostService(url, "Asset", {
      AssetId: tempFilterDetails.TruckId,
    });
  };

  const openFilterHandler = () => {
    // closePanelHandler();
    filterToggleHandler();
  };

  /**
   * This function provides filtering functionality of data on the basis of client id
   */
  const clientSelectHandler = (e: {
    label: string;
    value: number;
    key: number;
  }) => {
    let value = e.value;
    if (value < 0) toast.error(formatMessage("please_select_any_client_admin"));
    else {
      resetFilterHandler();
      setSelectedClientId(value);
      setPageInfo({ ...pageInfo, currentPage: 1 });
      setAmountDecimal(value);
      setEnableLimitSettings(value);
    }
  };

  const filterSubmitHandler = (values: any) => {
    // closePanelHandler();
    setPageInfo({ ...pageInfo, currentPage: 1 });
    setFilterDetail({ ...values, isFilterActive: true });
    filterToggleHandler();
  };
  const assetIdSubmitHandler = (values: any, apiType: number) => {
    // closePanelHandler();
    SendDownlinkAssetInformation(values, apiType);
    selectAssetIdsToggleHandler();
  };

  const resetFilterHandler = () => {
    setPageInfo({ ...pageInfo, currentPage: 1 });
    setFilterDetail({ ...truckInitialFilterState });
  };

  const addIcons = () => {
    let icon = [
      {
        handler: showDetailsClickHandler,
        icon: (
          <svg
            enableBackground="new 0 0 519.643 519.643"
            width="13"
            height="13"
            fill="white"
            viewBox="0 0 519.643 519.643"
          >
            <circle cx="259.823" cy="259.866" r="80" />
            <path d="m511.673 237.706c-61.494-74.31-154.579-145.84-251.85-145.84-97.29 0-190.397 71.58-251.85 145.84-10.63 12.84-10.63 31.48 0 44.32 15.45 18.67 47.84 54.71 91.1 86.2 108.949 79.312 212.311 79.487 321.5 0 43.26-31.49 75.65-67.53 91.1-86.2 10.599-12.815 10.654-31.438 0-44.32zm-251.85-89.84c61.76 0 112 50.24 112 112s-50.24 112-112 112-112-50.24-112-112 50.24-112 112-112z" />
          </svg>
        ),
        key: "",
        title: "label_downlink_asset_info",
        className: "btn-secondary",
      },
    ];

    let icon1 = [
      {
        handler: openAddUpdateHandler,
        icon: (
          <svg height="14" viewBox="0 0 492.49284 492" width="14">
            <path d="m304.140625 82.472656-270.976563 270.996094c-1.363281 1.367188-2.347656 3.09375-2.816406 4.949219l-30.035156 120.554687c-.898438 3.628906.167969 7.488282 2.816406 10.136719 2.003906 2.003906 4.734375 3.113281 7.527344 3.113281.855469 0 1.730469-.105468 2.582031-.320312l120.554688-30.039063c1.878906-.46875 3.585937-1.449219 4.949219-2.8125l271-270.976562zm0 0" />
            <path d="m476.875 45.523438-30.164062-30.164063c-20.160157-20.160156-55.296876-20.140625-75.433594 0l-36.949219 36.949219 105.597656 105.597656 36.949219-36.949219c10.070312-10.066406 15.617188-23.464843 15.617188-37.714843s-5.546876-27.648438-15.617188-37.71875zm0 0" />
          </svg>
        ),
        // key: "IsEditable",
        key: "",
        title: "label_edit",
        className: "btn-warning",
      },
      {
        handler: deleteClickHandler,
        icon: (
          <svg
            version="1.1"
            x="0px"
            y="0px"
            width="13"
            height="13"
            viewBox="0 0 512 512"
          >
            <g>
              <g>
                <path
                  d="M62.205,150l26.569,320.735C90.678,493.865,110.38,512,133.598,512h244.805c23.218,0,42.92-18.135,44.824-41.265
L449.795,150H62.205z M180.986,452c-7.852,0-14.458-6.108-14.956-14.063l-15-242c-0.513-8.276,5.771-15.395,14.033-15.908
c8.569-0.601,15.381,5.757,15.908,14.033l15,242C196.502,444.632,189.721,452,180.986,452z M271,437c0,8.291-6.709,15-15,15
c-8.291,0-15-6.709-15-15V195c0-8.291,6.709-15,15-15s15,6.709,15,15V437z M360.97,195.938l-15,242
c-0.493,7.874-7.056,14.436-15.908,14.033c-8.262-0.513-14.546-7.632-14.033-15.908l15-242
c0.513-8.276,7.764-14.297,15.908-14.033C355.199,180.543,361.483,187.662,360.97,195.938z"
                />
              </g>
            </g>
            <g>
              <g>
                <path
                  d="M451,60h-90V45c0-24.814-20.186-45-45-45H196c-24.814,0-45,20.186-45,45v15H61c-16.569,0-30,13.431-30,30
c0,16.567,13.431,30,30,30c137.966,0,252.039,0,390,0c16.569,0,30-13.433,30-30C481,73.431,467.569,60,451,60z M331,60H181V45
c0-8.276,6.724-15,15-15h120c8.276,0,15,6.724,15,15V60z"
                />
              </g>
            </g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
            <g></g>
          </svg>
        ),
        // key: "IsDeletable",
        key: "",
        title: "label_delete",
      },
    ];

    if (getUserType() === UserType.SuperAdmin) {
      return [...icon, ...icon1];
    } else {
      return icon;
    }
  };

  const columns = getColumns<ITruck, number>(
    showIcons(addIcons(), ModuleName.TankManagement).length > 0
      ? [
          {
            columnType: ColumnType.Hidden,
            accessorKey: "Id",
          },
          // {
          //   columnType: ColumnType.Sorting,
          //   accessorKey: "Name",
          //   sortingName: "truckname",
          //   displayName: "truckname",
          // },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "DeviceId",
            sortingName: "DeviceId",
            displayName: "transactions_label_deviceid",
          },
          // {
          //   columnType: ColumnType.Sorting,
          //   accessorKey: "AccountId",
          //   sortingName: "AccountId",
          //   displayName: "client_id",
          // },
          {
            columnType: ColumnType.ActionsFormatter,
            icons: showIcons(addIcons(), ModuleName.TankManagement),
            displayName: "label_action",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "AssetId",
            sortingName: "AssetId",
            displayName: "truck_id",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "AssetType",
            sortingName: "AssetType",
            displayName: "asset_type",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "Equipment",
            sortingName: "Equipment",
            displayName: "equipment",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "Compartment",
            sortingName: "Compartment",
            displayName: "compartment",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "InventorySource",
            sortingName: "InventorySource",
            displayName: "inventory_source",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "SimCard",
            sortingName: "SimCard",
            displayName: "sim_card",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "IpAddress",
            sortingName: "IpAddress",
            displayName: "ip_address",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "InstallationDate",
            sortingName: "InstallationDate",
            displayName: "installation_date",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "ExpiryDate",
            sortingName: "ExpiryDate",
            displayName: "expiry_date",
          },
        ]
      : [
          {
            columnType: ColumnType.Hidden,
            accessorKey: "Id",
          },
          // {
          //   columnType: ColumnType.Sorting,
          //   accessorKey: "Name",
          //   sortingName: "truckname",
          //   displayName: "truckname",
          // },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "DeviceId",
            sortingName: "DeviceId",
            displayName: "transactions_label_deviceid",
          },
          // {
          //   columnType: ColumnType.Sorting,
          //   accessorKey: "AccountId",
          //   sortingName: "AccountId",
          //   displayName: "client_id",
          // },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "AssetId",
            sortingName: "AssetId",
            displayName: "truck_id",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "AssetType",
            sortingName: "AssetType",
            displayName: "asset_type",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "Equipment",
            sortingName: "Equipment",
            displayName: "equipment",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "Compartment",
            sortingName: "Compartment",
            displayName: "compartment",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "InventorySource",
            sortingName: "InventorySource",
            displayName: "inventory_source",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "SimCard",
            sortingName: "SimCard",
            displayName: "sim_card",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "IpAddress",
            sortingName: "IpAddress",
            displayName: "ip_address",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "InstallationDate",
            sortingName: "InstallationDate",
            displayName: "installation_date",
          },
          {
            columnType: ColumnType.Sorting,
            accessorKey: "ExpiryDate",
            sortingName: "ExpiryDate",
            displayName: "expiry_date",
          },
        ],
    sorting,
    handleSortingChange
  );

  return (
    <React.Fragment>
      {showAddPopup && (
        <AddUpdateTruck id={selectedId} modalClosed={addToggleHandler} />
      )}
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={deleteHandler}
          modalClosed={deleteToggleHandler}
          isLoading={DeleteTruckState.inProgress}
          message="delete_truck_confirm"
          submitBtnText="submit_button"
          cancelBtnText="button_cancel"
          modalTitle="delete_truck"
        />
      )}

      {showFilterPopup && (
        <TruckFilterForm
          submitHandler={filterSubmitHandler}
          onModalClosed={filterToggleHandler}
          actionInProgress={DeleteTruckState.inProgress}
          initialValues={_.omit(filter, ["isFilterActive"])}
        />
      )}

      {showSelectAssetIdsPopup && (
        <SelectAssetIdForm
          submitHandler={assetIdSubmitHandler}
          onModalClosed={selectAssetIdsToggleHandler}
          actionInProgress={sendAssetInfoTruckState.inProgress}
          initialValues={{
            AssetDownlinkRequestItemArrayList: [],
          }}
          assetOptions={getTruckOptionsVehicle}
        />
      )}

      {/* {showFilterPopup && (
        <TruckFilterForm
          initialValues={_.omit(filter, ["isFilterActive"])}
          submitHandler={filterSubmitHandler}
          onModalClosed={filterToggleHandler}
          actionInProgress={false}
        />
      )} */}

      {/* <div className={`main-content user ${showUserDetail ? 'table-container' : ''} `}> */}
      <div className={`main-content user`}>
        <SubHeader
          moduleNameString={"trucks_title"}
          moduleName={ModuleName.TankManagement}
          addHandler={openAddUpdateHandler}
          isAdd={true && getUserType() === UserType.SuperAdmin}
          addButtonTitle="add_truck"
          filterHandler={openFilterHandler}
          isFilterActive={filter.isFilterActive}
          exportCSVHandler={exportFilteredLogsCSV}
          showClientSelectHandler={true && !openPanel}
          clientSelectHandler={clientSelectHandler}
          resetFilterHandler={resetFilterHandler}
          selectAssetIdHandler={selectAssetIdHandler}
          isFullView={false}
          isDownload={true}
          isRefresh={false}
          isFilter={true}
          isDateFilter={false}
          isImport={false}
          isMissing={false}
          isSelectAssetId={true && getUserType() === UserType.SuperAdmin}
          isDeleteAlarm={false}
        />
      </div>

      {inProgress && <SpinnerLoader />}
      <Container fluid className="p-0">
        {truckListData.length !== 0 ? (
          <div className="truckWrapper" id="moveContent">
            <div className={`${openPanel ? "paneIsOpen" : ""}`}>
              <div className="pt-3 px-3">
                <div className="Total-users d-flex justify-content-end mb-2">
                  <FormattedMessage id="total_tanks" /> :{" "}
                  <b className="ps-1"> {truckList.TotalAssets}</b>
                </div>
                <div className="Fuel-table">
                  <div
                    className="table-responsive"
                    style={getTableMaxHeight(height, width)}
                    data-testid="table"
                  >
                    <Grid
                      data={truckListData}
                      columns={columns}
                      isInProgress={inProgress}
                      hiddenColumns={["Id"]}
                    />
                  </div>
                </div>
                <Container fluid>
                  <Row className="tableFooter py-1">
                    <Col xs="3" className="d-flex">
                      <b className="text-white text-hidden-m mt-1 pt-1">
                        <FormattedMessage id="show" />:{" "}
                      </b>
                      <select
                        data-testid="pageSelect"
                        className="ms-2 from-control"
                        onChange={onPageLimitChangedForUser}
                        value={pageLimit}
                      >
                        {PageLimitOptions.map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      <Pagination
                        totalPages={pageInfo.totalPages}
                        pageLimit={pageLimit}
                        onPageChanged={onPageChanged}
                        disablePagination={disablePagination}
                      />
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
            <SlidingPanel
              type={"right"}
              isOpen={openPanel}
              noBackdrop={false}
              size={100}
            >
              <div>
                <div className="row g-0 border-bottom pt-3 pb-1 ps-3">
                  <Col className="d-flex" lg={10} md={6} xs={6}>
                    <Button
                      title="Send"
                      className="arrow-up-icon btn-primary btn d-flex px-2"
                      onClick={() => {
                        SendReadDownLinkAssetInformation(
                          selectedRow ?? {
                            Id: 0,
                            AssetId: "",
                            DeviceId: "",
                            Compartment: 0,
                            InventorySource: "",
                            SimCard: "",
                            IpAddress: "",
                            InstallationDate: "",
                            ExpiryDate: "",
                            AccountId: "",
                            TruckId: "",
                            AssetType: "",
                            Equipment: "",
                            IsDeletable: false,
                            IsEditable: false,
                          },
                          allAvailableClientOptions
                        );
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        fill="currentColor"
                        className="bi bi-arrow-up"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5v-11.793l3.646 3.647a.5.5 0 0 0 .708-.708l-4.5-4.5a.5.5 0 0 0-.708 0l-4.5 4.5a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                      </svg>
                      <FormattedMessage id="Read Downlink Information" />
                    </Button>
                  </Col>
                  <Col className="text-end" lg={2} md={6} xs={6}>
                    <span title="Refresh" className="refresh-report">
                      <svg
                        width="20"
                        onClick={() => {
                          ReadDownLinkAssetInformationForAssetId(
                            selectedRow ?? {
                              Id: 0,
                              AssetId: "",
                              DeviceId: "",
                              Compartment: 0,
                              InventorySource: "",
                              SimCard: "",
                              IpAddress: "",
                              InstallationDate: "",
                              ExpiryDate: "",
                              AccountId: "",
                              TruckId: "",
                              AssetType: "",
                              Equipment: "",
                              IsDeletable: false,
                              IsEditable: false,
                            },
                            allAvailableClientOptions
                          );
                        }}
                        height="20"
                        fill="currentColor"
                        className="bi bi-arrow-repeat m-0 mr-2 "
                        viewBox="0 0 16 16"
                      >
                        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                        <path
                          fillRule="evenodd"
                          d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                        />
                      </svg>
                    </span>
                    <svg
                      onClick={closePanelHandler}
                      role="button"
                      xlinkTitle="close"
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      fill="currentColor"
                      className="bi bi-x"
                      viewBox="0 0 16 16"
                    >
                      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                    </svg>
                  </Col>
                </div>
                <div className="group_detailsWrapper">
                  <TruckDetails selectedData={selectedRow} />
                </div>
              </div>
            </SlidingPanel>
          </div>
        ) : (
          <div className="no_data_table background-image">
            <div className="no_data w-100 text-center">
              <img src={NoData} alt="NOData" className="NOData" />
              <h1 className="fs-2">
                <FormattedMessage id="message_no_datafound" />
              </h1>
            </div>
          </div>
        )}
      </Container>
      <Footer />
    </React.Fragment>
  );
};

export default SelectAssetIdsPopupHOC(
  FilterPopupHOC(EditPopupHOC(AddPopupHOC(DeletePopupHOC(observer(Truck)))))
);
